/**
 * Wire shape for the agent's current working scope, as it crosses the
 * Socket.IO boundary between the FE and the message-gateway.
 *
 * The FE-side `Scope` discriminated union lives in `feature-project-page`
 * (URL is the canonical scope carrier). This module owns *only* the
 * wire-level shape: snake_case keys to match the BE convention, and a
 * Zod schema so callers can parse outgoing payloads at the trust boundary
 * before they hit the socket.
 *
 * The conversion `Scope → WireScope` lives next to the `Scope` type
 * (`feature-project-page/src/hooks/toWireScope.ts`) so the camelCase
 * domain model and its snake_case wire mirror stay co-located.
 */
import { z } from 'zod';

const ProjectScopeWireSchema = z.object({
  kind: z.literal('project'),
  project_id: z.string().min(1),
});

const CohortScopeWireSchema = z.object({
  kind: z.literal('cohort'),
  project_id: z.string().min(1),
  cohort_id: z.string().min(1),
});

const VariableScopeWireSchema = z.object({
  kind: z.literal('variable'),
  project_id: z.string().min(1),
  variable_id: z.string().min(1),
});

const ConceptGroupScopeWireSchema = z.object({
  kind: z.literal('concept_group'),
  project_id: z.string().min(1),
  concept_group_id: z.string().min(1),
});

const DatasetScopeWireSchema = z.object({
  kind: z.literal('dataset'),
  project_id: z.string().min(1),
  dataset_id: z.string().min(1),
});

/**
 * Draft scope kinds — added in OR-5963 step 2.
 *
 * Represent an in-flight artifact the agent has decided to focus on but
 * which doesn't exist as a persisted entity yet. ``client_id`` is a
 * server-minted UUID that lets the FE correlate this draft scope with
 * the eventual real scope (variable / cohort / concept_group) once the
 * artifact persists. ``slug`` / ``title`` are optional display hints;
 * neither is load-bearing for routing or filtering.
 *
 * The BE-driven ``STATUS_UPDATE { kind: 'focus_scope' }`` event is the
 * only entry point for these — they are never produced by URL parsing
 * (drafts have no URL representation today).
 */
// TODO: cleanup with research_assistant_gen_3 ff
const VariableDraftScopeWireSchema = z.object({
  kind: z.literal('variable_draft'),
  project_id: z.string().min(1),
  client_id: z.string().min(1),
  slug: z.string().optional(),
  title: z.string().optional(),
});

// TODO: cleanup with research_assistant_gen_3 ff
const CohortDraftScopeWireSchema = z.object({
  kind: z.literal('cohort_draft'),
  project_id: z.string().min(1),
  client_id: z.string().min(1),
  slug: z.string().optional(),
  title: z.string().optional(),
});

// TODO: cleanup with research_assistant_gen_3 ff
const ConceptGroupDraftScopeWireSchema = z.object({
  kind: z.literal('concept_group_draft'),
  project_id: z.string().min(1),
  client_id: z.string().min(1),
  slug: z.string().optional(),
  title: z.string().optional(),
});

export const WireScopeSchema = z.discriminatedUnion('kind', [
  ProjectScopeWireSchema,
  CohortScopeWireSchema,
  VariableScopeWireSchema,
  ConceptGroupScopeWireSchema,
  DatasetScopeWireSchema,
  VariableDraftScopeWireSchema,
  CohortDraftScopeWireSchema,
  ConceptGroupDraftScopeWireSchema,
]);

export type WireScope = z.infer<typeof WireScopeSchema>;
